import { Button, Checkbox, Flex, Select, TextArea, Text } from '@radix-ui/themes'
import React, { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  ClaimCenterBookableAndSchedule,
  ClaimCenterWithBookables,
  ResponseError,
  ScheduleExceptionTemplate,
} from '@Generated/back-office-client/src'
import { DEFAULT_STALE_DATA_TIMEOUT } from '@AppState/app-config'
import { TimeSelector } from '@Components/util/time-selection.tsx'
import { chain, cloneDeep, map, max, min, sortBy, sumBy } from 'lodash'
import { Form } from 'radix-ui'
import { ReoccurringEventSelector } from '@Components/util/reoccurring-event-selector.tsx'
import { convertDateToIsoDate } from '@Components/util/utils'
import { Modal } from '@Components/common/modal'
import { ClaimReservationsServiceAPI } from '@API/data-services/claim-reservations-service'

const isoDateRegex = /[0-9]{4}-[01][0-9]-[0-3][0-9]/

type ExceptionData = {
  claimCenterId: string
  bookableId?: string
  localDate: string
  localStartTime: string
  localEndTime: string
  notes: string
}

type ExceptionFormValidation = {
  dateValid: boolean
  timesValid: boolean
  repeatValid: boolean
  notesValid: boolean
  formSubmitting: boolean
  submitProgress: number
  submitTotal: number
}

const defaultValidationState: ExceptionFormValidation = {
  dateValid: true,
  timesValid: true,
  repeatValid: true,
  notesValid: true,
  formSubmitting: false,
  submitProgress: 0,
  submitTotal: 0,
}

function countMinutes(isoTimeStart: string, isoTimeEnd: string): number {
  const [startHour, startMinute] = isoTimeStart.split(':').map((n) => parseInt(n))
  const [endHour, endMinute] = isoTimeEnd.split(':').map((n) => parseInt(n))

  const hourDiff = (endHour - startHour) * 60
  const minuteDiff = endMinute - startMinute

  return hourDiff + minuteDiff
}

export const ScheduleUnavailability = ({
  onOpenChange,
  activeClaimCenter,
  currentDate,
  refetchGrid,
}: {
  onOpenChange: (open: boolean) => void
  activeClaimCenter: ClaimCenterWithBookables | null
  currentDate: Date
  refetchGrid: () => Promise<unknown>
}) => {
  const [validation, setValidation] = useState<ExceptionFormValidation>(defaultValidationState)
  const [selectedDate, setSelectedDate] = useState<string>(convertDateToIsoDate(currentDate))
  const [selectedNumberOfColumns, setSelectedNumberOfColumns] = useState<number>(1)

  const [minStartTime, setMinStartTime] = useState<string>('00:00:00')
  const [maxStartTime, setMaxStartTime] = useState<string>('24:00:00')
  const [selectedStartTime, setSelectedStartTime] = useState<string>()
  const [minEndTime, setMinEndTime] = useState<string>('00:00:00')
  const [maxEndTime, setMaxEndTime] = useState<string>('24:00:00')
  const [selectedEndTime, setSelectedEndTime] = useState<string>()
  const minuteSelections = ['00', '15', '30', '45']
  const [selectedAllDay, setSelectedAllDay] = useState<boolean>(false)
  const [selectedCancelExisting, setSelectedCancelExsisting] = useState<boolean>(false)

  const [repeatDates, setRepeatDates] = useState<string[]>([])
  const [repeatEndDate, setRepeatEndDate] = useState<string>()

  const { data: bookables } = useQuery({
    queryKey: [`${activeClaimCenter?.id}-bookables-${selectedDate}`],
    staleTime: DEFAULT_STALE_DATA_TIMEOUT,
    queryFn: async () => {
      return await ClaimReservationsServiceAPI.getClaimCenterBookablesForDate(
        activeClaimCenter!.id,
        selectedDate,
      )
    },
    enabled: !!activeClaimCenter?.id && !!selectedDate,
  })

  const { mutateAsync: saveException } = useMutation({
    mutationFn: async (exceptionData: ExceptionData) => {
      const exception: ScheduleExceptionTemplate = {
        localDate: exceptionData.localDate,
        localStartTime: exceptionData.localStartTime,
        localEndTime: exceptionData.localEndTime,
        reason: exceptionData.notes,
      }

      if (exceptionData.bookableId) {
        await ClaimReservationsServiceAPI.createClaimCenterBookableException(
          exceptionData.claimCenterId,
          exceptionData.bookableId,
          exception,
        )
      } else {
        await ClaimReservationsServiceAPI.createClaimCenterException(
          exceptionData.claimCenterId,
          exception,
        )
      }
    },
  })

  const { mutateAsync: cancelReservation } = useMutation({
    mutationFn: async (reservationId: string) => {
      try {
        return await ClaimReservationsServiceAPI.cancelReservation(reservationId)
      } catch (e) {
        // If we tried to delete a reservation in the past, that's okay, just ignore it
        if (e instanceof ResponseError && e.response.status !== 422) {
          throw e
        }
      }
    },
  })

  useEffect(() => {
    if (bookables) {
      const minTime =
        chain(bookables)
          .filter((b) => b.schedules.length > 0)
          .flatMap((b) => b.schedules)
          .minBy((s) => s.localStartTime)
          .value()?.localStartTime ?? '00:00:00'

      const maxTime =
        chain(bookables)
          .filter((b) => b.schedules.length > 0)
          .flatMap((b) => b.schedules)
          .maxBy((s) => s.localEndTime)
          .value()?.localEndTime ?? '24:00:00'

      setMinStartTime(minTime)
      setMaxStartTime(selectedEndTime || maxTime)
      setMinEndTime(selectedStartTime || minTime)
      setMaxEndTime(maxTime)
    }
  }, [bookables, selectedStartTime, selectedEndTime])

  function onSetSelectedDate(event: React.ChangeEvent<HTMLInputElement>) {
    setSelectedDate(event.target.value)
  }

  function onSetRepeatEndDate(event: React.ChangeEvent<HTMLInputElement>) {
    setRepeatEndDate(event.target.value)
  }

  async function prioritizeBookables(
    date: string,
    blockableBookables: ClaimCenterBookableAndSchedule[],
    startTime: string,
    endTime: string,
  ) {
    const latestReservationData = await ClaimReservationsServiceAPI.getReservations(
      activeClaimCenter!.id,
      date,
      date,
    )
    const latestExceptionData = await ClaimReservationsServiceAPI.getClaimCenterExceptionsForDate(
      activeClaimCenter!.id,
      date,
    )

    const bookablesWithReservationInfo = map(blockableBookables, (b) => {
      // For ordering what bookables we block off with exceptions, we have two priorities:
      // 1. Block off time periods with no exceptions (only reservations) first, ranked by
      // most unbooked minutes
      // 2. Block off time periods with existing exceptions, ranked by most unbooked
      // minutes
      const alreadyScheduledReservations = latestReservationData.filter((r) => {
        return (
          r.timeslot.display.isoEndTime > startTime && r.timeslot.display.isoStartTime < endTime
        )
      })
      let reservationMinutes = sumBy(alreadyScheduledReservations, (r) => {
        const start = max([startTime, r.timeslot.display.isoStartTime])!
        const end = min([endTime, r.timeslot.display.isoEndTime])!
        return countMinutes(start, end)
      })

      const alreadyScheduledExceptions = [
        ...latestExceptionData.claimCenterExceptions,
        ...latestExceptionData.bookableExceptions,
      ].filter((exception) => {
        if (
          'claimCenterId' in exception ||
          ('claimCenterBookableId' in exception &&
            exception.claimCenterBookableId === b.bookable.id)
        ) {
          return exception.localEndTime > startTime && exception.localStartTime < endTime
        }
      })
      const exceptionMinutes = sumBy(alreadyScheduledExceptions, (ex) => {
        const start = max([startTime, ex.localStartTime])!
        const end = min([endTime, ex.localEndTime])!
        return countMinutes(start, end)
      })

      if (exceptionMinutes !== 0) {
        reservationMinutes = 10000 + exceptionMinutes + reservationMinutes
      }

      return {
        bookable: b,
        reservations: alreadyScheduledReservations,
        bookedMinutes: reservationMinutes,
      }
    })

    return sortBy(bookablesWithReservationInfo, (b) => b.bookedMinutes)
  }

  return (
    <Modal
      onOpenChange={onOpenChange}
      title="Schedule Unavailability"
      description="Schedule unavailability for dates"
      hideTitle={false}
      className="schedule-unavailability-dialog">
      <Form.Root
        onSubmit={async (e) => {
          e.preventDefault()

          const formData = new FormData(e.currentTarget).entries()
          const data = Object.fromEntries(formData)

          let startTime = '00:00:00'
          let endTime = '23:59:59'
          if (!selectedAllDay) {
            startTime = selectedStartTime!
            endTime = selectedEndTime!
          }

          const validation: ExceptionFormValidation = cloneDeep(defaultValidationState)

          if (!isoDateRegex.test(selectedDate)) {
            validation.dateValid = false
          }

          if (!(startTime && endTime) && !selectedAllDay) {
            validation.timesValid = false
          }

          if (repeatDates.length === 0) {
            validation.repeatValid = false
          }

          if (!data.notes) {
            validation.notesValid = false
          }

          if (
            !(
              validation.dateValid &&
              validation.timesValid &&
              validation.repeatValid &&
              validation.notesValid
            )
          ) {
            setValidation(validation)
            return
          }

          validation.formSubmitting = true
          validation.submitTotal = repeatDates.length
          setValidation(validation)
          for (const date of repeatDates) {
            validation.submitProgress += 1
            setValidation(validation)

            const blockableBookables = chain(bookables)
              .filter((b) => b.schedules.length > 0)
              .sortBy((b) => -b.bookable.sortOrder)
              .value()

            const prioritizedBookables = await prioritizeBookables(
              date,
              blockableBookables,
              startTime,
              endTime,
            )

            if (selectedNumberOfColumns === Infinity) {
              const exceptionData: ExceptionData = {
                claimCenterId: activeClaimCenter!.id,
                localDate: date,
                localStartTime: startTime,
                localEndTime: endTime,
                notes: data.notes as string,
              }

              await saveException(exceptionData)

              if (selectedCancelExisting) {
                const reservationsToCancel = prioritizedBookables.flatMap((b) => b.reservations)
                for (const reservationToCancel of reservationsToCancel) {
                  await cancelReservation(reservationToCancel.id)
                }
              }
            } else {
              for (let i = 0; i < selectedNumberOfColumns; i++) {
                const bookableData = prioritizedBookables[i]
                if (bookableData) {
                  const exceptionData: ExceptionData = {
                    claimCenterId: activeClaimCenter!.id,
                    bookableId: bookableData.bookable.bookable.id,
                    localDate: date,
                    localStartTime: startTime,
                    localEndTime: endTime,
                    notes: data.notes as string,
                  }

                  await saveException(exceptionData)
                  if (selectedCancelExisting) {
                    const reservationsToCancel = bookableData.reservations
                    for (const reservationToCancel of reservationsToCancel) {
                      await cancelReservation(reservationToCancel.id)
                    }
                  }
                }
              }
            }
          }

          await refetchGrid()
          onOpenChange(false)
        }}>
        <Flex justify="between" gap="3">
          <Flex direction="column" justify="start">
            <Form.Field name="date" className="form-field">
              <Form.Label>Select Date</Form.Label>
              <Form.Control asChild className={validation.dateValid ? '' : 'invalid'}>
                <input
                  type="date"
                  className="rt-reset rt-SelectTrigger rt-r-size-2 rt-variant-surface"
                  defaultValue={selectedDate}
                  onChange={onSetSelectedDate}
                />
              </Form.Control>
            </Form.Field>

            <Form.Field name="startTime" className="form-field">
              <Form.Label>Select Start Time</Form.Label>
              <Form.Control asChild>
                <TimeSelector
                  startTime={minStartTime}
                  endTime={maxStartTime}
                  minuteIncrements={minuteSelections}
                  selectedTime={selectedStartTime}
                  onValueChange={setSelectedStartTime}
                  disabled={selectedAllDay}
                  className={validation.timesValid ? '' : 'invalid'}
                />
              </Form.Control>
            </Form.Field>

            <Form.Field name="allDay" className="form-field">
              <Flex>
                <Form.Control asChild className={validation.timesValid ? '' : 'invalid'}>
                  <Checkbox
                    className="form-checkbox"
                    checked={selectedAllDay}
                    onClick={() => setSelectedAllDay(!selectedAllDay)}
                  />
                </Form.Control>
                <Form.Label className="checkbox-label-right">All Day</Form.Label>
              </Flex>
            </Form.Field>

            <Form.Field name="repeat" className="form-field">
              <Form.Label>Repeat</Form.Label>
              <Form.Control asChild className={validation.repeatValid ? '' : 'invalid'}>
                <ReoccurringEventSelector
                  startDate={selectedDate}
                  endDate={repeatEndDate}
                  onDatesChanged={setRepeatDates}
                />
              </Form.Control>
            </Form.Field>
          </Flex>

          <Flex direction="column" justify="start">
            <Form.Field name="numberOfColumns" className="form-field">
              <Form.Label>Select Number of Columns</Form.Label>
              <Form.Control asChild>
                <Select.Root
                  defaultValue={selectedNumberOfColumns.toString()}
                  onValueChange={(value) => setSelectedNumberOfColumns(parseFloat(value))}>
                  <Select.Trigger />
                  <Select.Content>
                    {bookables?.map((bookable, i) => {
                      if (bookable.schedules.length > 0) {
                        const displayValue = i + 1

                        return (
                          <Select.Item key={displayValue} value={displayValue.toString()}>
                            {displayValue}
                          </Select.Item>
                        )
                      }
                    })}
                    <Select.Item value="Infinity">All</Select.Item>
                  </Select.Content>
                </Select.Root>
              </Form.Control>
            </Form.Field>

            <Form.Field name="endTime" className="form-field">
              <Form.Label>Select End Time</Form.Label>
              <Form.Control asChild>
                <TimeSelector
                  startTime={minEndTime}
                  endTime={maxEndTime}
                  minuteIncrements={minuteSelections}
                  selectedTime={selectedEndTime}
                  onValueChange={setSelectedEndTime}
                  disabled={selectedAllDay}
                  className={validation.timesValid ? '' : 'invalid'}
                />
              </Form.Control>
            </Form.Field>

            <div className="form-spacer"></div>

            <Form.Field name="repeat-end" className="form-field">
              <Form.Label>Until</Form.Label>
              <Form.Control asChild className={validation.repeatValid ? '' : 'invalid'}>
                <input
                  type="date"
                  className="rt-reset rt-SelectTrigger rt-r-size-2 rt-variant-surface"
                  onChange={onSetRepeatEndDate}
                />
              </Form.Control>
            </Form.Field>
          </Flex>
        </Flex>

        <Form.Field name="notes" className="form-field">
          <Form.Label>Notes</Form.Label>
          <Form.Control asChild className={validation.notesValid ? '' : 'invalid'}>
            <TextArea rows={4}></TextArea>
          </Form.Control>
        </Form.Field>

        <Form.Field name="cancelExisting" className="form-field">
          <Flex>
            <Form.Control asChild className={validation.timesValid ? '' : 'invalid'}>
              <Checkbox
                className="form-checkbox"
                checked={selectedCancelExisting}
                onClick={() => setSelectedCancelExsisting(!selectedCancelExisting)}
              />
            </Form.Control>
            <Form.Label className="checkbox-label-right">
              Cancel all appointments during this time. This will automatically text a cancellation
              message and a prompt to reschedule to players.
              <br />
              <Text size="1">
                <Text weight="bold">Note:</Text> Only future reservations will be cancelled
              </Text>
            </Form.Label>
          </Flex>
        </Form.Field>

        <Flex gap="3" justify="center">
          <ul className="schedule-validation">
            {validation.dateValid ? null : <li>You must select a date</li>}
            {validation.timesValid ? null : (
              <li>You must select a time range, or select &quot;All Day&quot;</li>
            )}
            {validation.repeatValid ? null : (
              <li>You must select an end date for your repeating event</li>
            )}
            {validation.notesValid ? null : <li>You must enter a note</li>}
          </ul>
          {validation.formSubmitting ? (
            <p className="schedule-unavailability-progress">
              Saving {validation.submitProgress} of {validation.submitTotal} events
            </p>
          ) : null}
        </Flex>

        <Flex gap="3" justify="center" className="schedule-unavailability-dialog-controls">
          <Form.Submit asChild>
            <Button disabled={validation.formSubmitting}>Submit</Button>
          </Form.Submit>
        </Flex>
      </Form.Root>
    </Modal>
  )
}
